import type { OnChange } from '@monaco-editor/react'
import { Editor, loader } from '@monaco-editor/react'
import { useCallback, useEffect, useRef } from 'react'

interface JsonEditorProps {
  value: string
  onChange?: OnChange
  baseOptions: any
  theme?: string
  height?: number | string
  width?: number | string
  schema?: object | null
  onValidationChange?: (isValid: boolean) => void
}

/**
 * Monaco JSON Editor with schema validation
 */
export function JsonEditor({
  value,
  onChange,
  baseOptions,
  theme,
  height = 500,
  width,
  schema,
  onValidationChange,
}: Readonly<JsonEditorProps>) {
  const markersListenerRef = useRef<any>(null)
  const editorRef = useRef<any>(null)
  const onValidationChangeRef = useRef(onValidationChange)
  onValidationChangeRef.current = onValidationChange

  const isReadOnly = baseOptions.readOnly

  useEffect(() => {
    if (schema) {
      configureMonaco()
    }
  }, [schema])

  const configureMonaco = async () => {
    try {
      const monacoInstance = await loader.init()

      monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        allowComments: false,
        trailingCommas: 'error',
        schemas: [
          {
            uri: 'appConfigSchema.json',
            fileMatch: ['*'],
            schema: schema,
          },
        ],
      })

      if (markersListenerRef.current) {
        markersListenerRef.current.dispose()
      }

      markersListenerRef.current = monacoInstance.editor.onDidChangeMarkers(() => {
        const editorModel = editorRef.current?.getModel()
        if (editorModel && onValidationChangeRef.current) {
          const markers = monacoInstance.editor.getModelMarkers({ resource: editorModel.uri })
          const hasErrors = markers.some((marker: any) => marker.severity === 4 || marker.severity === 8)
          onValidationChangeRef.current(!hasErrors)
        }
      })
    } catch (error) {
      console.error('Monaco configuration failed:', error)
    }
  }

  const handleEditorDidMount = useCallback((editor: any) => {
    editorRef.current = editor
  }, [])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (markersListenerRef.current) {
        markersListenerRef.current.dispose()
      }
    }
  }, [])

  const jsonEditorOptions = {
    ...baseOptions,
    hover: { enabled: true },
    parameterHints: { enabled: true },
    tabSize: 2,
    ...(isReadOnly
      ? {}
      : {
          autoIndent: 'full' as const,
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          acceptSuggestionOnEnter: 'on' as const,
          tabCompletion: 'on' as const,
          insertSpaces: true,
          detectIndentation: true,
          folding: true,
          autoClosingBrackets: 'always' as const,
          autoClosingQuotes: 'always' as const,
          matchBrackets: 'always' as const,
        }),
  }

  return (
    <Editor
      height={height}
      language="json"
      options={jsonEditorOptions}
      theme={theme}
      value={value}
      width={width}
      onChange={onChange}
      onMount={handleEditorDidMount}
    />
  )
}
